"use client";

/**
 * ContactOperationDisplay - CRUD operation results for contacts
 */

import { useState } from "react";
import Link from "next/link";
import { Tag, Alert } from "@/ui";
import { spacing, textStyles } from "@/ui/tokens";
import { actionLinkClass } from "../utils";
import { Business, Visibility, Edit } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  cleanErrorMessage,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface OperationOutput {
  success?: boolean;
  status?: "success" | "error";
  message?: string;
  contact?: {
    id: string | number;
    name?: string;
    first_name?: string;
    last_name?: string;
  };
  urls?: {
    view?: string;
    edit?: string;
  };
  error?: string;
}

export function ContactOperationDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as OperationOutput | undefined;
  const isSuccess = result?.success === true || result?.status === "success";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title=""
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Business style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {isLoading && (
          <div style={{ padding: `${spacing[2]}px 0`, textAlign: "center" }}>
            <ToolProgress variant="dots" label="Processing..." />
          </div>
        )}

        {isError && error && <ToolError error={error} />}

        {!isLoading && result && (
          <Alert
            variant={isSuccess ? "success" : "error"}
            title={isSuccess ? "Success" : "Error"}
          >
            <span style={{ ...textStyles.body2 }}>
              {isSuccess
                ? (result.message || "Operation completed successfully")
                : cleanErrorMessage(result.message || result.error || "Operation failed")}
            </span>
            {result.contact && (
              <div style={{ marginTop: spacing[2], display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
                <Tag size="sm" color="neutral">
                  {result.contact.name ||
                    (result.contact.first_name && result.contact.last_name
                      ? `${result.contact.first_name} ${result.contact.last_name}`
                      : `Contact #${result.contact.id}`)}
                </Tag>
                {result.urls?.view && (
                  <Link
                    href={result.urls.view}
                    className={actionLinkClass}
                  >
                    <Visibility style={{ fontSize: 14 }} /> View Contact
                  </Link>
                )}
                {result.urls?.edit && (
                  <Link
                    href={result.urls.edit}
                    className={actionLinkClass}
                  >
                    <Edit style={{ fontSize: 14 }} /> Edit Contact
                  </Link>
                )}
              </div>
            )}
          </Alert>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default ContactOperationDisplay;
